import type { JSONSchemaType } from 'ajv'
import type { ActionDefinition, ActionOutcome } from '../action'
import { getAction, registerAction } from '../registry'

export const DESIGN_NODE_EDIT_ACTION_ID = 'design.node.edit'
export const DESIGN_NODE_DUPLICATE_ACTION_ID = 'design.node.duplicate'
export const DESIGN_NODE_CHANGE_ROLE_ACTION_ID = 'design.node.change-role'
export const DESIGN_NODE_DELETE_ACTION_ID = 'design.node.delete'

export const DESIGN_ROLE_CYCLE_ACTION_ID = 'design.role.cycle'
export const DESIGN_ROLE_SHOW_DOWNSTREAM_ACTION_ID = 'design.role.show-downstream'
export const DESIGN_ROLE_DETACH_ACTION_ID = 'design.role.detach'

export const DESIGN_CANVAS_NEW_NODE_ACTION_ID = 'design.canvas.new-node'
export const DESIGN_CANVAS_REFLOW_ACTION_ID = 'design.canvas.reflow'
export const DESIGN_CANVAS_SHOW_BINDINGS_ACTION_ID = 'design.canvas.show-bindings'

export type DesignOrbitTargetArgs = {
  id?: string
  title?: string
}

const designOrbitTargetArgsSchema: JSONSchemaType<DesignOrbitTargetArgs> = {
  type: 'object',
  properties: {
    id: { type: 'string', nullable: true },
    title: { type: 'string', nullable: true },
  },
  required: [],
  additionalProperties: true,
}

const TARGET_REQUIRED_ACTION_IDS = new Set([
  DESIGN_NODE_EDIT_ACTION_ID,
  DESIGN_NODE_DUPLICATE_ACTION_ID,
  DESIGN_NODE_CHANGE_ROLE_ACTION_ID,
  DESIGN_NODE_DELETE_ACTION_ID,
  DESIGN_ROLE_CYCLE_ACTION_ID,
  DESIGN_ROLE_SHOW_DOWNSTREAM_ACTION_ID,
  DESIGN_ROLE_DETACH_ACTION_ID,
])

const makeOrbitDesignAction = (
  id: string,
  label: string,
  description: string,
  risk: 'READ_ONLY' | 'LOW' | 'MEDIUM' | 'HIGH',
  permissionAction?: 'board.edit' | 'timeline.view',
): ActionDefinition<DesignOrbitTargetArgs> => ({
  id,
  label,
  description,
  risk,
  domain: 'design',
  surfaces: ['orbit', 'catalog'],
  argsSchema: designOrbitTargetArgsSchema,
  permission: permissionAction ? (context) => context.canPerform(permissionAction) : undefined,
  run: async (args, context): Promise<ActionOutcome> => {
    const correlationId = context.correlationId ?? 'orbit-correlation'

    if (TARGET_REQUIRED_ACTION_IDS.has(id) && !args.id) {
      const message = `Target ID is required for ${label}`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    const ops = context.designOps
    let handler: (() => Promise<void> | void) | undefined

    if (id === DESIGN_NODE_EDIT_ACTION_ID) {
      handler = ops?.editShape ? () => ops.editShape!(args.id!) : undefined
    } else if (id === DESIGN_NODE_DUPLICATE_ACTION_ID) {
      handler = ops?.duplicateShape ? () => ops.duplicateShape!(args.id!) : undefined
    } else if (id === DESIGN_NODE_CHANGE_ROLE_ACTION_ID) {
      handler = ops?.changeRole ? () => ops.changeRole!(args.id!) : undefined
    } else if (id === DESIGN_NODE_DELETE_ACTION_ID) {
      handler = ops?.deleteShape ? () => ops.deleteShape!(args.id!) : undefined
    } else if (id === DESIGN_ROLE_CYCLE_ACTION_ID) {
      handler = ops?.cycleRole ? () => ops.cycleRole!(args.id!) : undefined
    } else if (id === DESIGN_ROLE_SHOW_DOWNSTREAM_ACTION_ID) {
      handler = ops?.showDownstream ? () => ops.showDownstream!(args.id!) : undefined
    } else if (id === DESIGN_ROLE_DETACH_ACTION_ID) {
      handler = ops?.detachRole ? () => ops.detachRole!(args.id!) : undefined
    } else if (id === DESIGN_CANVAS_NEW_NODE_ACTION_ID) {
      handler = ops?.createNode ? () => ops.createNode!() : undefined
    } else if (id === DESIGN_CANVAS_REFLOW_ACTION_ID) {
      handler = ops?.reflow ? () => ops.reflow!() : undefined
    } else if (id === DESIGN_CANVAS_SHOW_BINDINGS_ACTION_ID) {
      handler = ops?.showBindings ? () => ops.showBindings!() : undefined
    }

    if (!handler) {
      const message = `Operation for ${label} (${id}) is not available`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    try {
      await Promise.resolve(handler())
    } catch (err) {
      const message = err instanceof Error ? err.message : `Failed to execute ${label}`
      context.showToast?.(message, 'error')
      return {
        kind: 'FAILED',
        message,
        recoverable: false,
        correlationId,
      }
    }

    context.showToast?.(`${label}${args.title ? ` · ${args.title}` : ''}`, 'info')
    return {
      kind: 'COMPLETED',
      result: { id: args.id, title: args.title },
      message: label,
      correlationId,
    }
  },
})

const designNodeEditAction = makeOrbitDesignAction(
  DESIGN_NODE_EDIT_ACTION_ID,
  'Edit',
  'Edits the selected design canvas node.',
  'LOW',
  'board.edit',
)

const designNodeDuplicateAction = makeOrbitDesignAction(
  DESIGN_NODE_DUPLICATE_ACTION_ID,
  'Duplicate',
  'Duplicates the selected design canvas node.',
  'LOW',
  'board.edit',
)

const designNodeChangeRoleAction = makeOrbitDesignAction(
  DESIGN_NODE_CHANGE_ROLE_ACTION_ID,
  'Change role',
  'Changes the role of the selected design canvas node.',
  'LOW',
  'board.edit',
)

const designNodeDeleteAction = makeOrbitDesignAction(
  DESIGN_NODE_DELETE_ACTION_ID,
  'Delete',
  'Deletes the selected design canvas node.',
  'MEDIUM',
  'board.edit',
)

const designRoleCycleAction = makeOrbitDesignAction(
  DESIGN_ROLE_CYCLE_ACTION_ID,
  'Cycle role',
  'Cycles through assigned roles for the node.',
  'LOW',
  'board.edit',
)

const designRoleShowDownstreamAction = makeOrbitDesignAction(
  DESIGN_ROLE_SHOW_DOWNSTREAM_ACTION_ID,
  'Show downstream',
  'Visualizes downstream nodes and dependencies from this role.',
  'READ_ONLY',
  'timeline.view',
)

const designRoleDetachAction = makeOrbitDesignAction(
  DESIGN_ROLE_DETACH_ACTION_ID,
  'Detach',
  'Detaches the role from the selected node.',
  'LOW',
  'board.edit',
)

const designCanvasNewNodeAction = makeOrbitDesignAction(
  DESIGN_CANVAS_NEW_NODE_ACTION_ID,
  'New node',
  'Creates a new node on the design canvas.',
  'LOW',
  'board.edit',
)

const designCanvasReflowAction = makeOrbitDesignAction(
  DESIGN_CANVAS_REFLOW_ACTION_ID,
  'Reflow',
  'Reflows and realigns nodes across the design canvas.',
  'LOW',
  'board.edit',
)

const designCanvasShowBindingsAction = makeOrbitDesignAction(
  DESIGN_CANVAS_SHOW_BINDINGS_ACTION_ID,
  'Show all bindings',
  'Displays all bindings and connections across the canvas.',
  'READ_ONLY',
  'timeline.view',
)

export const registerDesignActions = (): void => {
  if (!getAction(DESIGN_NODE_EDIT_ACTION_ID)) registerAction(designNodeEditAction)
  if (!getAction(DESIGN_NODE_DUPLICATE_ACTION_ID)) registerAction(designNodeDuplicateAction)
  if (!getAction(DESIGN_NODE_CHANGE_ROLE_ACTION_ID)) registerAction(designNodeChangeRoleAction)
  if (!getAction(DESIGN_NODE_DELETE_ACTION_ID)) registerAction(designNodeDeleteAction)
  if (!getAction(DESIGN_ROLE_CYCLE_ACTION_ID)) registerAction(designRoleCycleAction)
  if (!getAction(DESIGN_ROLE_SHOW_DOWNSTREAM_ACTION_ID)) registerAction(designRoleShowDownstreamAction)
  if (!getAction(DESIGN_ROLE_DETACH_ACTION_ID)) registerAction(designRoleDetachAction)
  if (!getAction(DESIGN_CANVAS_NEW_NODE_ACTION_ID)) registerAction(designCanvasNewNodeAction)
  if (!getAction(DESIGN_CANVAS_REFLOW_ACTION_ID)) registerAction(designCanvasReflowAction)
  if (!getAction(DESIGN_CANVAS_SHOW_BINDINGS_ACTION_ID)) registerAction(designCanvasShowBindingsAction)
}

registerDesignActions()
